const DAY_MS = 24 * 60 * 60 * 1000;

// Close promotion acquisition while preserving existing participants' progress and payouts.
export const AUTHOR_PROMOTION_ENABLED = false;
export const AUTHOR_PROMOTION_CAMPAIGN_KEY = "author-challenge-2026";
export const AUTHOR_PROMOTION_TERMS_VERSION = "author-challenge-2026-v3";
export const AUTHOR_PROMOTION_PAYOUT_CENTS = 3_000;
export const AUTHOR_PROMOTION_MIN_WORDS = 1_000;
export const AUTHOR_PROMOTION_CHAPTERS_PER_WINDOW = 1;
export const AUTHOR_PROMOTION_WINDOW_DAYS = 7;
export const AUTHOR_PROMOTION_TOTAL_WINDOWS = 12;
export const AUTHOR_PROMOTION_NOTIFICATION_TITLE = "Post 1 chapter a week, earn $30";
export const AUTHOR_PROMOTION_NOTIFICATION_BODY =
    "Publish one qualifying chapter a week for 12 weeks to earn $30 USD.";

type AuthorPromotionProgressStatus =
    | "not_started"
    | "on_track"
    | "disqualified"
    | "earned"
    | "ended_incomplete";

interface AuthorPromotionWindow {
    number: number;
    startsAt: Date;
    endsAt: Date;
    chapterCount: number;
    isComplete: boolean;
    isCurrent: boolean;
    isPast: boolean;
}

export interface AuthorPromotionProgress {
    startsAt: Date;
    endsAt: Date;
    windows: AuthorPromotionWindow[];
    status: AuthorPromotionProgressStatus;
    completedWindows: number;
    currentWindowNumber: number | null;
    currentWindowRemaining: number;
    totalQualifyingChapters: number;
    daysRemaining: number;
    chaptersPerWindow: number;
    windowDays: number;
}

interface BuildAuthorPromotionProgressInput {
    startsAt: Date;
    endsAt?: Date;
    windowDays?: number;
    totalWindows?: number;
    chaptersPerWindow?: number;
    qualifyingPublishedAt: Date[];
    now?: Date;
}

export function authorPromotionEndsAt(startsAt: Date): Date {
    return new Date(
        startsAt.getTime() +
            AUTHOR_PROMOTION_WINDOW_DAYS * AUTHOR_PROMOTION_TOTAL_WINDOWS * DAY_MS,
    );
}

export function buildAuthorPromotionProgress(
    input: BuildAuthorPromotionProgressInput,
): AuthorPromotionProgress {
    const now = input.now ?? new Date();
    const windowDays = input.windowDays ?? AUTHOR_PROMOTION_WINDOW_DAYS;
    const totalWindows = input.totalWindows ?? AUTHOR_PROMOTION_TOTAL_WINDOWS;
    const chaptersPerWindow =
        input.chaptersPerWindow ?? AUTHOR_PROMOTION_CHAPTERS_PER_WINDOW;
    const endsAt = input.endsAt ?? authorPromotionEndsAt(input.startsAt);
    const validPublicationTimes = input.qualifyingPublishedAt
        .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()))
        .map((date) => date.getTime());

    const windows = Array.from(
        { length: totalWindows },
        (_, index): AuthorPromotionWindow => {
            const startsAt = new Date(
                input.startsAt.getTime() + index * windowDays * DAY_MS,
            );
            const windowEndsAt = new Date(
                startsAt.getTime() + windowDays * DAY_MS,
            );
            const chapterCount = validPublicationTimes.filter(
                (publishedAt) =>
                    publishedAt >= startsAt.getTime() &&
                    publishedAt < windowEndsAt.getTime(),
            ).length;

            return {
                number: index + 1,
                startsAt,
                endsAt: windowEndsAt,
                chapterCount,
                isComplete: chapterCount >= chaptersPerWindow,
                isCurrent:
                    now.getTime() >= startsAt.getTime() &&
                    now.getTime() < windowEndsAt.getTime(),
                isPast: now.getTime() >= windowEndsAt.getTime(),
            };
        },
    );

    const completedWindows = windows.filter((window) => window.isComplete).length;
    const currentWindow = windows.find((window) => window.isCurrent) ?? null;
    const hasMissedPastWindow = windows.some(
        (window) => window.isPast && !window.isComplete,
    );
    const hasEnded = now.getTime() >= endsAt.getTime();
    const hasStarted = now.getTime() >= input.startsAt.getTime();
    const status: AuthorPromotionProgressStatus = !hasStarted
        ? "not_started"
        : hasEnded
        ? completedWindows === totalWindows
            ? "earned"
            : "ended_incomplete"
        : hasMissedPastWindow
          ? "disqualified"
          : "on_track";

    return {
        startsAt: input.startsAt,
        endsAt,
        windows,
        status,
        completedWindows,
        currentWindowNumber: currentWindow?.number ?? null,
        currentWindowRemaining: currentWindow
            ? Math.max(
                  0,
                  chaptersPerWindow - currentWindow.chapterCount,
              )
            : 0,
        totalQualifyingChapters: windows.reduce(
            (total, window) => total + window.chapterCount,
            0,
        ),
        daysRemaining: Math.max(
            0,
            Math.ceil((endsAt.getTime() - now.getTime()) / DAY_MS),
        ),
        chaptersPerWindow,
        windowDays,
    };
}
